import { Alert, Box, Button, Chip, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { BRAND_PRIMARY, RADIUS_LG, RADIUS_SM, SIDEBAR } from "../../theme/theme";

// ---------------------------------------------------------------------------
// Shape of the `ask_user` tool arguments — mirrors the TypeBox schema in
// `be/argus-ts/src/extensions/ask-user.ts`. Kept as a plain type on the FE
// to avoid a cross-package import for a schema runtime library.
// ---------------------------------------------------------------------------

export type AskUserQuestionType = "single_select" | "multi_select" | "free_text";

/**
 * Normalize LLM-generated type strings to our canonical types.
 * The LLM may send "select", "radio", "multiple", "checkbox", etc.
 */
function normalizeQuestionType(raw: string, hasOptions: boolean): AskUserQuestionType {
  const t = raw.toLowerCase().replace(/[_-\s]/g, "");
  if (t === "multiselect" || t === "multiple" || t === "checkbox" || t === "checkboxes") {
    return "multi_select";
  }
  if (
    t === "singleselect" ||
    t === "select" ||
    t === "radio" ||
    t === "choice" ||
    t === "multiplechoice"
  ) {
    return "single_select";
  }
  if (t === "freetext" || t === "text" || t === "input" || t === "open") {
    return "free_text";
  }
  // Fallback: if options are provided, treat as single_select
  return hasOptions ? "single_select" : "free_text";
}

export interface AskUserOption {
  label: string;
  description?: string;
}

export interface AskUserQuestion {
  id: string;
  question: string;
  type: string; // raw from LLM — normalized at render time
  options?: AskUserOption[];
}

export interface AskUserArgs {
  questions: AskUserQuestion[];
}

export type AskUserStatus = "pending" | "submitting" | "answered" | "timed_out";

export interface ArgusAskUserBlockProps {
  toolCallId: string;
  args: AskUserArgs;
  status: AskUserStatus;
  /** Persisted answers from the tool result — available on transcript reload. */
  persistedAnswers?: { id: string; value: string }[];
  /** True when the question was dismissed/skipped (from persisted tool result). */
  persistedDismissed?: boolean;
  onSubmit: (answers: unknown[]) => Promise<void>;
  onDismiss: () => Promise<void>;
}

interface AnswerDraft {
  value: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Short label for tab text, e.g. "Q1: Environment" */
function tabLabel(question: AskUserQuestion, index: number): string {
  // Derive a short label from the question text (first ~3 words or 25 chars)
  const words = question.question.split(/\s+/).slice(0, 3).join(" ");
  const short = words.length > 25 ? `${words.slice(0, 22)}…` : words;
  return `Q${index + 1}: ${short}`;
}

/**
 * Renders a single `ask_user` tool call as an inline chat block.
 *
 * Pencil: Frame 3 (`vCVJ7`).
 *
 * One tab per question (when multiple). Each question renders a
 * type-appropriate input:
 *   - single_select → card-style radio options
 *   - multi_select  → card-style checkbox options
 *   - free_text     → multiline text field
 *
 * The submit button is disabled until every question has a non-empty
 * answer. On submit, `onSubmit` is awaited; the component tracks its own
 * submit lifecycle (idle → submitting → answered) via `localStatus` since
 * the crash-proof ask_user mechanic (abort+placeholder+replaceMessages)
 * does not emit a second `tool_execution_end` SSE event after the user
 * answers.
 */
export const ArgusAskUserBlock = ({
  toolCallId,
  args,
  status,
  persistedAnswers,
  persistedDismissed,
  onSubmit,
  onDismiss,
}: ArgusAskUserBlockProps) => {
  const questions = args.questions ?? [];
  const [activeTab, setActiveTab] = useState(0);
  const [dismissed, setDismissed] = useState(persistedDismissed ?? false);
  const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>(() =>
    Object.fromEntries(questions.map((q) => [q.id, { value: "" }])),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<"idle" | "submitting" | "answered">("idle");

  const effectiveStatus: AskUserStatus =
    status === "timed_out" || status === "answered"
      ? status
      : localStatus === "answered"
        ? "answered"
        : localStatus === "submitting"
          ? "submitting"
          : status;

  const allAnswered = useMemo(
    () => questions.every((q) => (drafts[q.id]?.value ?? "").trim().length > 0),
    [questions, drafts],
  );

  const updateDraft = (id: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [id]: { value } }));
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setLocalStatus("submitting");
    const answers = questions.map((q) => ({
      id: q.id,
      value: drafts[q.id]?.value ?? "",
    }));
    try {
      await onSubmit(answers);
      setLocalStatus("answered");
    } catch (err) {
      setLocalStatus("idle");
      setSubmitError(err instanceof Error ? err.message : "Failed to submit answer");
    }
  };

  const handleDismiss = async () => {
    setSubmitError(null);
    setLocalStatus("submitting");
    setDismissed(true);
    try {
      await onDismiss();
      setLocalStatus("answered");
    } catch (err) {
      setLocalStatus("idle");
      setDismissed(false);
      setSubmitError(err instanceof Error ? err.message : "Failed to dismiss question");
    }
  };

  const isDone = effectiveStatus === "answered" || effectiveStatus === "timed_out";
  const isMultiQuestion = questions.length > 1;
  const isLastTab = activeTab >= questions.length - 1;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: isDone ? "divider" : "rgba(0,0,0,0.12)",
        borderRadius: `${RADIUS_LG}px`,
        backgroundColor: "background.paper",
        overflow: "hidden",
        width: "100%",
        opacity: isDone ? 0.85 : 1,
      }}
      data-argus-tool-call-id={toolCallId}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          padding: "12px 16px",
          borderBottom: isDone ? undefined : `1px solid ${SIDEBAR.divider}`,
        }}
      >
        {/* Purple ? icon */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            backgroundColor: isDone ? "action.disabled" : BRAND_PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              color: "common.white",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            ?
          </Typography>
        </Box>

        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, flex: 1, color: "text.primary", fontSize: 14 }}
        >
          {isDone ? "Argus asked" : "Argus needs your input"}
        </Typography>

        {/* Status badges */}
        {effectiveStatus === "submitting" && <Chip size="small" label="Submitting" />}
        {effectiveStatus === "answered" && !dismissed && (
          <Chip size="small" label="Answered" color="success" />
        )}
        {effectiveStatus === "answered" && dismissed && <Chip size="small" label="Skipped" />}
        {effectiveStatus === "timed_out" && <Chip size="small" label="Timed out" color="warning" />}

        {/* Question count badge (pending only, multiple questions) */}
        {!isDone && isMultiQuestion && (
          <Box
            sx={{
              backgroundColor: "background.default",
              borderRadius: `${RADIUS_SM}px`,
              padding: "2px 8px",
            }}
          >
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {questions.length} questions
            </Typography>
          </Box>
        )}
      </Stack>

      {/* ── Answered / Done state ──────────────────────────────────── */}
      {isDone ? (
        <Box sx={{ padding: "12px 16px" }}>
          <Stack spacing={1}>
            {questions.map((question, index) => {
              const draftAnswer = (drafts[question.id]?.value ?? "").trim();
              const persistedAnswer =
                persistedAnswers?.find((a) => a.id === question.id)?.value ?? "";
              const answer = draftAnswer || persistedAnswer;
              return (
                <Box key={question.id}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Q{isMultiQuestion ? index + 1 : ""}
                      {isMultiQuestion ? ". " : " "}
                    </Box>
                    {question.question}
                  </Typography>
                  {answer ? (
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: 0.25,
                        paddingLeft: 1.5,
                        borderLeft: "2px solid",
                        borderColor: "success.main",
                        color: "text.primary",
                      }}
                    >
                      {answer}
                    </Typography>
                  ) : dismissed ? (
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: 0.25,
                        paddingLeft: 1.5,
                        color: "text.disabled",
                        fontStyle: "italic",
                      }}
                    >
                      Skipped
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : (
        <>
          {/* ── Tabs (multi-question only) ───────────────────────── */}
          {isMultiQuestion && (
            <Box
              sx={{
                padding: "0 16px",
                borderBottom: `1px solid ${SIDEBAR.divider}`,
                display: "flex",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 0,
                  "& .MuiTabs-indicator": { display: "none" },
                }}
              >
                {questions.map((q, index) => (
                  <Tab
                    key={q.id}
                    label={tabLabel(q, index)}
                    sx={{
                      minHeight: 0,
                      padding: "10px 14px",
                      fontSize: 12,
                      fontWeight: index === activeTab ? 500 : 400,
                      color: index === activeTab ? BRAND_PRIMARY : "text.secondary",
                      textTransform: "none",
                      borderBottom:
                        index === activeTab
                          ? `2px solid ${BRAND_PRIMARY}`
                          : "2px solid transparent",
                      "&.Mui-selected": { color: BRAND_PRIMARY },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {/* ── Question body ────────────────────────────────────── */}
          {questions.map((question, index) => {
            if (index !== activeTab && isMultiQuestion) return null;
            const draft = drafts[question.id] ?? { value: "" };
            const qType = normalizeQuestionType(question.type, Boolean(question.options?.length));
            return (
              <Box key={question.id} sx={{ padding: "16px 16px 12px 16px" }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "text.primary",
                    marginBottom: 0.5,
                  }}
                >
                  {question.question}
                </Typography>

                {/* Hint text for select types */}
                {qType === "single_select" && question.options && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      marginBottom: 1.5,
                    }}
                  >
                    Select one option
                  </Typography>
                )}
                {qType === "multi_select" && question.options && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      marginBottom: 1.5,
                    }}
                  >
                    Select one or more options
                  </Typography>
                )}

                {/* ── single_select: card-style radios ─────────── */}
                {qType === "single_select" && question.options ? (
                  <Stack spacing={0.75}>
                    {question.options.map((opt) => {
                      const isSelected = draft.value === opt.label;
                      return (
                        <OptionCard
                          key={opt.label}
                          label={opt.label}
                          description={opt.description}
                          isSelected={isSelected}
                          disabled={effectiveStatus === "submitting"}
                          variant="radio"
                          onClick={() => updateDraft(question.id, opt.label)}
                        />
                      );
                    })}
                  </Stack>
                ) : qType === "multi_select" && question.options ? (
                  /* ── multi_select: card-style checkboxes ────────── */
                  <Stack spacing={0.75}>
                    {question.options.map((opt) => {
                      const selected = draft.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const isSelected = selected.includes(opt.label);
                      return (
                        <OptionCard
                          key={opt.label}
                          label={opt.label}
                          description={opt.description}
                          isSelected={isSelected}
                          disabled={effectiveStatus === "submitting"}
                          variant="checkbox"
                          onClick={() => {
                            const next = isSelected
                              ? selected.filter((s) => s !== opt.label)
                              : [...selected, opt.label];
                            updateDraft(question.id, next.join(", "));
                          }}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  /* ── free_text: multiline text field ────────────── */
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={draft.value}
                    disabled={effectiveStatus === "submitting"}
                    placeholder="Your answer…"
                    onChange={(e) => updateDraft(question.id, e.target.value)}
                    size="small"
                    sx={{ marginTop: 0.5 }}
                  />
                )}
              </Box>
            );
          })}

          {/* ── Error ────────────────────────────────────────────── */}
          {submitError && (
            <Alert severity="error" sx={{ margin: "0 16px 8px", fontSize: "0.8rem" }}>
              {submitError}
            </Alert>
          )}

          {/* ── Footer ───────────────────────────────────────────── */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              padding: "12px 16px",
              borderTop: `1px solid ${SIDEBAR.divider}`,
            }}
          >
            {/* Page indicator */}
            {isMultiQuestion ? (
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {activeTab + 1} of {questions.length}
              </Typography>
            ) : (
              <Box />
            )}

            {/* Action buttons */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                disabled={effectiveStatus === "submitting"}
                onClick={() => void handleDismiss()}
                sx={{
                  color: "text.secondary",
                  borderColor: "rgba(0,0,0,0.12)",
                  borderRadius: `${RADIUS_SM}px`,
                  textTransform: "none",
                  fontSize: 13,
                  padding: "6px 16px",
                  "&:hover": {
                    borderColor: "rgba(0,0,0,0.24)",
                    backgroundColor: "rgba(0,0,0,0.02)",
                  },
                }}
              >
                Skip
              </Button>
              {isMultiQuestion && !isLastTab ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setActiveTab((t) => Math.min(t + 1, questions.length - 1))}
                  sx={{
                    backgroundColor: BRAND_PRIMARY,
                    borderRadius: `${RADIUS_SM}px`,
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 500,
                    padding: "6px 16px",
                    "&:hover": { backgroundColor: "primary.dark" },
                  }}
                >
                  {"Next \u2192"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  disabled={!allAnswered || effectiveStatus === "submitting"}
                  onClick={() => void handleSubmit()}
                  sx={{
                    backgroundColor: BRAND_PRIMARY,
                    borderRadius: `${RADIUS_SM}px`,
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 500,
                    padding: "6px 16px",
                    "&:hover": { backgroundColor: "primary.dark" },
                  }}
                >
                  {effectiveStatus === "submitting" ? "Submitting…" : "Submit"}
                </Button>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// OptionCard — shared card-style option for both radio and checkbox
// ---------------------------------------------------------------------------

function OptionCard({
  label,
  description,
  isSelected,
  disabled,
  variant,
  onClick,
}: {
  label: string;
  description?: string;
  isSelected: boolean;
  disabled: boolean;
  variant: "radio" | "checkbox";
  onClick: () => void;
}) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      role={variant === "radio" ? "radio" : "checkbox"}
      aria-checked={isSelected}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 14px",
        borderRadius: `${RADIUS_SM + 2}px`,
        border: isSelected ? `1.5px solid ${BRAND_PRIMARY}` : "1px solid rgba(0,0,0,0.12)",
        backgroundColor: isSelected ? "background.default" : "background.paper",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": disabled
          ? {}
          : {
              borderColor: isSelected ? BRAND_PRIMARY : "rgba(0,0,0,0.24)",
              backgroundColor: isSelected ? "background.default" : "action.hover",
            },
      }}
    >
      {/* Indicator */}
      {variant === "radio" ? (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            border: isSelected ? `2px solid ${BRAND_PRIMARY}` : "1.5px solid rgba(0,0,0,0.2)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: BRAND_PRIMARY,
              }}
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "3px",
            border: isSelected ? `2px solid ${BRAND_PRIMARY}` : "1.5px solid rgba(0,0,0,0.2)",
            backgroundColor: isSelected ? BRAND_PRIMARY : "transparent",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <svg
              width="10"
              height="8"
              viewBox="0 0 10 8"
              fill="none"
              role="img"
              aria-label="Selected"
            >
              <path
                d="M1 4L3.5 6.5L9 1"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
      )}

      {/* Label + description */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          minWidth: 0,
        }}
      >
        <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.4 }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.3 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
